/**
 * ChildPreviousRules.js
 * Pure business logic for Child Previous Vaccination scenario
 *
 * Phase 4: Age-based checkbox + date picker logic for recording vaccinations
 * that were administered in the past (outside the current session).
 *
 * WHY THIS IS DIFFERENT FROM CHILD CURRENT:
 * - child-current: "Is today a valid day to administer this dose?" → radio buttons
 * - child-previous: "On what past date was this dose administered?" → checkbox + date picker
 *
 * The checkbox/date-picker model requires TWO separate gates per dose:
 *   1. Checkbox gate   – can this dose even appear as selectable?
 *   2. Date range gate – what is the valid [minDate, maxDate] window for the picker?
 *
 * The checkbox is enabled when:
 *   a) The dose is NOT already recorded in the vaccination history
 *   b) The dose's valid date window (DOB + lower_age_limit → min(DOB + upper_age_limit, today))
 *      has at least one day that lies in the past (i.e., minDate <= today)
 *   c) Sequential dependency is met (previous dose is recorded OR already selected in the form)
 *
 * Age-expired behaviour:
 * Unlike child-current, an age-expired dose in child-previous is MORE nuanced.
 * If upper_age_limit < today, the dose's window is capped at that date.
 * If the window [lower, min(upper, today)] has a valid range, the dose CAN still be
 * recorded as a historical entry – it just cannot be administered today.
 * So 'AgeExpired' in this scenario means: "the dose window is 100% in the past,
 * but the checkbox is still enabled if a valid window exists."
 *
 * DoseConfig contract returned by getChildPreviousDoseStatus:
 * {
 *   status           : 'administered' | 'enabled' | 'disabled' | 'no_window' | 'Unknown'
 *   checkboxEnabled  : boolean    -- true only when the checkbox should be interactive
 *   showCheckbox     : boolean    -- true when the checkbox widget should render at all
 *   showDatePicker   : boolean    -- true when the date picker should render (after checkbox check)
 *   minDate          : Date|null  -- earliest valid date for the date picker
 *   maxDate          : Date|null  -- latest valid date for the date picker
 *   date             : string|null -- formatted 'DD/MM/YYYY' when status === 'administered'
 *   reason           : string|null -- human-readable explanation for disabled / no_window
 *   ageInDays        : number     -- child's current age in days
 * }
 *
 * NOTE: This file intentionally re-exports parseDob and calculateChildAgeInDays
 * from child-current rather than duplicating them. All other logic here is
 * Previous-specific and has no equivalent in child-current.
 */

import dayjs from 'dayjs';

// Re-use the canonical age utilities from Phase 3 rather than copying them.
// This is the ONLY cross-scenario import allowed – it avoids duplicating the
// multi-format DOB parsing logic that already has test coverage in child-current.
import {
  parseDob,
  calculateChildAgeInDays,
  findAdministeredDose,
  checkSequentialDependency as checkSequentialDependencyFromCurrent,
  checkConflicts
} from '../children-current/ChildCurrentRules';

// Re-export so consumers of child-previous have a single import path
export { parseDob, calculateChildAgeInDays };

// ============================================================
// SECTION 1: Date window calculation
// ============================================================

/**
 * Calculate the valid date window for recording a Previous dose.
 *
 * WHY THIS DIFFERS FROM CHILD CURRENT'S APPROACH:
 * child-current only asks "is today inside the window?" (a boolean).
 * child-previous needs the actual [minDate, maxDate] range so the date picker
 * can constrain which calendar days the user can select.
 *
 * Window construction:
 *   minDate = DOB + lower_age_limit (earliest the dose could have been given)
 *   maxDate = min(DOB + upper_age_limit, today)
 *             (latest the dose could have been given – cannot be in the future,
 *              and cannot exceed the age window cutoff)
 *
 * Dose frequency constraint (applied on top of age-based minDate):
 *   When doseIndex > 0, if the previous dose has a known date (either from the
 *   vaccination history or from the form's selectedDoses array), then
 *   minDate = max(minDate, prevDoseDate + dose_frequency).
 *   This ensures the recorded gap matches the clinical schedule.
 *
 * @param {number}      doseIndex     - Zero-based dose index
 * @param {Object}      material      - Full material object from all-uip-materials API
 * @param {string}      childDob      - Child's date of birth string (any supported format)
 * @param {Array|null}  selectedDoses - Doses currently selected in the form
 *                                      Used to apply frequency constraint even before saving
 * @param {Array|null}  administeredDoses - Previously recorded vaccination history
 * @param {Function}    decryptFunction   - Injected decryption function for dose numbers
 * @returns {{
 *   minDate : Date|null,
 *   maxDate : Date|null,
 *   isValid : boolean,   -- false when the window has zero valid days
 *   reason  : string|null
 * }}
 */
export const calculateDoseDateWindow = (
  doseIndex,
  material,
  childDob,
  selectedDoses = null,
  administeredDoses = null,
  decryptFunction = null,
  identityContext = null
) => {
  const maxAllowedDate = dayjs().subtract(1, 'day');

  if (!childDob || childDob === '') {
    return {
      minDate: null,
      maxDate: null,
      isValid: false,
      reason: 'Child date of birth is not available'
    };
  }

  const dobDate = parseDob(childDob);

  // Guard: if DOB cannot be parsed, no window is calculable
  if (!dobDate.isValid()) {
    console.error('ChildPreviousRules: could not parse DOB:', childDob);
    return {
      minDate: null,
      maxDate: null,
      isValid: false,
      reason: `Child date of birth could not be parsed: "${childDob}"`
    };
  }

  // Guard: if the material has no age limit arrays, fall back to the
  // permissive window DOB → yesterday. This keeps the feature working even when
  // the API response is incomplete.
  if (!material || !Array.isArray(material.lower_age_limits)) {
    return {
      minDate: dobDate.toDate(),
      maxDate: maxAllowedDate.toDate(),
      isValid: true,
      reason: null
    };
  }

  const isPrecaution = typeof material.no_of_doses === 'number' && doseIndex >= material.no_of_doses;
  const pIndex = isPrecaution ? doseIndex - material.no_of_doses : 0;

  const lowerAgeArray = isPrecaution && material.no_of_precaution_doses > 0
    ? material.precaution_lower_age_limits
    : material.lower_age_limits;

  const upperAgeArray = isPrecaution && material.no_of_precaution_doses > 0
    ? material.precaution_upper_age_limits
    : material.upper_age_limits;

  const targetIndex = isPrecaution ? pIndex : doseIndex;

  const lowerAgeDays = lowerAgeArray?.[targetIndex] ?? 0;
  const upperAgeDays = upperAgeArray?.[targetIndex] ?? 0; // 0 = no upper limit

  // Base min: the earliest this dose could have been administered
  let minDate = dobDate.add(lowerAgeDays, 'day');

  // Base max: the lesser of the age-window cutoff and yesterday.
  // We never allow today or a date in the future for previous vaccinations.
  let maxDate;
  if (upperAgeDays > 0) {
    const cutoffDate = dobDate.add(upperAgeDays, 'day');
    maxDate = cutoffDate.isBefore(maxAllowedDate, 'day') ? cutoffDate : maxAllowedDate;
  } else {
    maxDate = maxAllowedDate;
  }

  // Dose-frequency constraint: for dose N (index > 0), the previous dose date
  // sets a floor on minDate.
  if (doseIndex > 0) {
    const prevDoseDate = _resolvePreviousDoseDate(
      doseIndex,
      material,
      childDob,
      selectedDoses,
      administeredDoses,
      decryptFunction,
      identityContext
    );

    if (prevDoseDate) {
      const freqArray = isPrecaution && material.no_of_precaution_doses > 0
        ? material.precaution_dose_frequencies
        : material.dose_frequencies;
      const frequency = freqArray?.[targetIndex] ?? 28; // Default 28 days
      const minByFrequency = prevDoseDate.add(frequency, 'day');

      // Take the later of the two lower bounds
      if (minByFrequency.isAfter(minDate)) {
        minDate = minByFrequency;
      }
    }
    // If prevDoseDate is null, the frequency constraint cannot be applied yet.
    // The sequential-dependency check (in getChildPreviousDoseStatus) will
    // disable the checkbox before we reach the date picker, so this is safe.
  }

  // Sanity check: if minDate > maxDate, there are no valid recording dates
  if (minDate.isAfter(maxDate, 'day')) {
    const displayLower = material.display_lower_age_limits?.[doseIndex];
    const displayUpper = material.display_upper_age_limits?.[doseIndex];

    let reason;
    if (minDate.isAfter(maxAllowedDate, 'day')) {
      // The dose window hasn't opened yet (child is too young)
      reason = null;
    } else {
      // The dose window was entirely in the past but is now invalid due to frequency
      reason = `No valid recording dates exist for this dose`;
    }

    return { minDate: null, maxDate: null, isValid: false, reason };
  }

  return {
    minDate: minDate.toDate(),
    maxDate: maxDate.toDate(),
    isValid: true,
    reason: null
  };
};

/**
 * PRIVATE helper: Resolve the previous dose's administration date.
 *
 * Looks in two places in priority order:
 *   1. selectedDoses (the form's in-progress selections) – user may have just
 *      checked a previous dose without saving yet, so we honour that.
 *   2. administeredDoses (the vaccination history from the API).
 *
 * This is intentionally private (underscore prefix) because it is an
 * implementation detail of calculateDoseDateWindow and should not be called
 * directly from outside this module.
 *
 * @param {number}      doseIndex         - Zero-based index of the CURRENT dose
 * @param {Object}      material          - Material object (used for material_name lookup)
 * @param {Array|null}  selectedDoses     - Form's currently selected doses
 * @param {Array|null}  administeredDoses - Vaccination history
 * @param {Function}    decryptFunction   - Injected decrypt function
 * @returns {dayjs.Dayjs|null}            - dayjs object for the previous dose date, or null
 */
const _resolvePreviousDoseDate = (
  doseIndex,
  material,
  childDob,
  selectedDoses,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  const dobDate = parseDob(childDob);
  if (!dobDate.isValid()) return null;

  // We need to find the administration date for the "previous" dose (doseIndex - 1).
  // If THAT dose is missing a date, we recursively find the date for (doseIndex - 2),
  // and so on, until we find a real date or hit the DOB.
  
  const calculateAssumedDateForIndex = (idx) => {
    if (idx < 0) return dobDate;

    // 1. Check current form selections (highest priority)
    if (selectedDoses && selectedDoses[idx] && selectedDoses[idx].selected) {
      if (selectedDoses[idx].administeredDate) {
        const parsed = dayjs(selectedDoses[idx].administeredDate);
        if (parsed.isValid()) return parsed;
      }
      // Selected but no date: compute assumed = max(prev_assumed + freq, DOB + lower_limit)
      const prevAssumedDate = calculateAssumedDateForIndex(idx - 1);
      const frequency = material?.dose_frequencies?.[idx] ?? 28;
      const lowerLimit = material?.lower_age_limits?.[idx] ?? 0;
      const dateByFreq = prevAssumedDate.add(frequency, 'day');
      const dateByAge = dobDate.add(lowerLimit, 'day');
      return dateByFreq.isAfter(dateByAge) ? dateByFreq : dateByAge;
    }

// 2. Check historical records
if (administeredDoses && decryptFunction && material?.material_name) {
    const { isAdministered, vaccinationDate } = findAdministeredDose(
    idx,
    material.material_name,
    administeredDoses,
    decryptFunction,
    identityContext
    );

    // If it's administered AND has a real date, return it
    if (isAdministered && vaccinationDate) {
    const parsed = dayjs(vaccinationDate);
    if (parsed.isValid()) return parsed;
    }

      // 3. If it's administered but MISSING a date, calculate assumed date
      if (isAdministered) {
        const prevAssumedDate = calculateAssumedDateForIndex(idx - 1);
        const frequency = material.dose_frequencies?.[idx] ?? 28;
        const lowerLimit = material.lower_age_limits?.[idx] ?? 0;
        
        // Assumed = max(Previous + Frequency, DOB + LowerLimit)
        const dateByFreq = prevAssumedDate.add(frequency, 'day');
        const dateByAge = dobDate.add(lowerLimit, 'day');
        
        return dateByFreq.isAfter(dateByAge) ? dateByFreq : dateByAge;
      }
    }

    return null;
  };

  return calculateAssumedDateForIndex(doseIndex - 1);
};

// ============================================================
// SECTION 2: Sequential dependency check (Previous-specific)
// ============================================================

/**
 * Check whether the sequential dependency for a Previous dose is satisfied.
 *
 * The rule is the same as in child-current (dose N-1 must be recorded before
 * dose N can be added), but the check here expands to also accept a dose that
 * the user has selected in the CURRENT form session – even if it has not been
 * saved to the API yet.
 *
 * This allows the user to record dose 1 and dose 2 in a single form submission
 * without having to save and re-open the form.
 *
 * @param {number}      doseIndex         - Zero-based dose index to check
 * @param {string}      materialName      - material_name to filter records
 * @param {Array|null}  administeredDoses - Vaccination history
 * @param {Array|null}  selectedDoses     - Doses currently selected in the form
 * @param {Function}    decryptFunction   - Injected decrypt function
 * @returns {{ pass: boolean, reason: string|null }}
 */
export const checkPreviousSequentialDependency = (
  doseIndex,
  materialName,
  administeredDoses,
  selectedDoses,
  decryptFunction,
  identityContext = null
) => {
  // First dose has no predecessor
  if (doseIndex === 0) {
    return { pass: true, reason: null };
  }

  const prevIndex = doseIndex - 1;

  // Check 1: Is the previous dose checked in the form?
  // Only `selected === true` is required – a date is NOT needed to unlock the next dose.
  // The next dose's minDate is derived from the assumed date when no explicit date is entered.
  const prevSelectedInForm =
    selectedDoses &&
    selectedDoses[prevIndex]?.selected === true;

  if (prevSelectedInForm) {
    return { pass: true, reason: null };
  }

  // Check 2: Is the previous dose in the administration history?
  // Delegate to the shared helper from child-current so the decrypt logic
  // stays in one place.
  const historyCheck = checkSequentialDependencyFromCurrent(
    doseIndex,
    materialName,
    administeredDoses || [],
    decryptFunction || (() => '0'),
    identityContext
  );

  if (historyCheck.pass) {
    return { pass: true, reason: null };
  }

  // Neither check passed
  return {
    pass: false,
    reason: null
  };
};

// ============================================================
// SECTION 3: Core status function (the main public API)
// ============================================================

/**
 * MAIN ENTRY POINT: Determine the DoseConfig for a single dose in the
 * Child Previous vaccination scenario.
 *
 * Decision tree:
 *   1. Already administered? → status: 'administered', no checkbox rendered
 *   2. Date window invalid?  → status: 'no_window',    checkbox disabled (impossible to record)
 *   3. Sequential dep fails? → status: 'disabled',     checkbox disabled (need prior dose)
 *   4. All checks pass       → status: 'enabled',      checkbox enabled, date picker shown
 *
 * WHY THE ORDER MATTERS:
 * - "Administered" must be first because a recorded dose should always show
 *   its date, even if the age window has now expired (historical fact).
 * - "No window" comes before sequential dep because if there are no valid
 *   dates at all, the reason is "impossible window" not "missing prior dose."
 *   Showing the wrong reason would confuse the user.
 * - Sequential dep is last so it is only surfaced when everything else is fine.
 *
 * @param {Object}      vaccine           - Vaccine object { id, name, availableDoses, material }
 * @param {number}      doseIndex         - Zero-based dose index
 * @param {string}      childDob          - Child's DOB string
 * @param {Array}       administeredDoses - Vaccination history from the profile API
 * @param {Function}    decryptFunction   - Stable decryption function (wrap in useCallback)
 * @param {Array|null}  selectedDoses     - Current form selections (for seq dep + frequency)
 * @returns {Object} DoseConfig - see contract at top of file
 */
export const getChildPreviousDoseStatus = (
  vaccine,
  doseIndex,
  childDob,
  administeredDoses,
  decryptFunction,
  selectedDoses = null,
  previousVaccines = [],
  identityContext = null
) => {
  const material = vaccine.material || null;
  const materialName = vaccine.name;
  const childAgeInDays = calculateChildAgeInDays(childDob);

  // ---- Check 0: Conflicts ----
  const conflictCheck = checkConflicts(
    materialName,
    administeredDoses,
    decryptFunction,
    previousVaccines,
    identityContext
  );
  if (!conflictCheck.pass) {
    return {
      status: 'disabled',
      checkboxEnabled: false,
      showCheckbox: true,
      showDatePicker: false,
      minDate: null,
      maxDate: null,
      date: null,
      reason: conflictCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 1: Already administered? ----
  // Show the recorded date and skip all further logic. Historical facts are
  // immutable from this form's perspective.
  const { isAdministered, vaccinationDate } = findAdministeredDose(
    doseIndex,
    materialName,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (isAdministered) {
    return {
      status: 'administered',
      checkboxEnabled: false,
      showCheckbox: false, // Don't show checkbox for already-recorded doses
      showDatePicker: false,
      minDate: null,
      maxDate: null,
      date: vaccinationDate ? dayjs(vaccinationDate).format('DD/MM/YYYY') : null,
      reason: null,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 2: Date window ----
  // Calculate whether a valid recording window exists at all.
  const window = calculateDoseDateWindow(
    doseIndex,
    material,
    childDob,
    selectedDoses,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!window.isValid) {
    // The window has no valid days. The checkbox appears but is disabled
    // so the user can see the dose exists and understand why it is locked.
    return {
      status: 'no_window',
      checkboxEnabled: false,
      showCheckbox: true,
      showDatePicker: false,
      minDate: null,
      maxDate: null,
      date: null,
      reason: window.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 3: Sequential dependency ----
  // The date window exists, but the previous dose must be present first.
  const seqCheck = checkPreviousSequentialDependency(
    doseIndex,
    materialName,
    administeredDoses,
    selectedDoses,
    decryptFunction,
    identityContext
  );

  if (!seqCheck.pass) {
    return {
      status: 'disabled',
      checkboxEnabled: false,
      showCheckbox: true,
      showDatePicker: false,
      minDate: null,
      maxDate: null,
      date: null,
      reason: seqCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- All checks pass: dose is recordable ----
  return {
    status: 'enabled',
    checkboxEnabled: true,
    showCheckbox: true,
    // showDatePicker is true here to signal "this dose CAN have a date picker"
    // but the UI only renders the picker when the checkbox is actually checked.
    // Keeping it true on the config simplifies the render condition:
    //   showDatePicker && dose.selected  →  render picker
    showDatePicker: true,
    minDate: window.minDate,
    maxDate: window.maxDate,
    date: null,
    reason: null,
    ageInDays: childAgeInDays
  };
};

// ============================================================
// SECTION 4: Date picker utilities
// ============================================================

/**
 * Determine whether a specific calendar day should be disabled in the
 * MUI DatePicker (the shouldDisableDate prop).
 *
 * This is a pure utility function – it has no side effects and can be
 * called inside or outside React components. It mirrors the equivalent
 * function in PWPreviousRules.js for API consistency.
 *
 * @param {Date}      date    - The calendar day being evaluated by the picker
 * @param {Date|null} minDate - Earliest valid date from the DoseConfig
 * @param {Date|null} maxDate - Latest valid date from the DoseConfig
 * @returns {boolean} - true means the day is disabled (greyed out)
 */
export const shouldDisableDate = (date, minDate, maxDate) => {
  // If constraints are missing, disable everything (safe default)
  if (!minDate || !maxDate) return true;

  const check = dayjs(date);
  return check.isBefore(dayjs(minDate), 'day') || check.isAfter(dayjs(maxDate), 'day');
};

/**
 * Build the complete date picker constraint object for a dose.
 *
 * Consumers can spread this directly into the MUI DatePicker props:
 *   const constraints = getDatePickerConstraints(doseConfig);
 *   <DatePicker minDate={constraints.minDate} maxDate={constraints.maxDate} ... />
 *
 * @param {Object} doseConfig - DoseConfig from getChildPreviousDoseStatus
 * @returns {{ shouldDisableDate: Function, minDate: dayjs.Dayjs|null, maxDate: dayjs.Dayjs|null }}
 */
export const getDatePickerConstraints = (doseConfig) => {
  if (!doseConfig.showDatePicker || !doseConfig.minDate || !doseConfig.maxDate) {
    return {
      shouldDisableDate: () => true,
      minDate: null,
      maxDate: null
    };
  }

  return {
    shouldDisableDate: (date) => shouldDisableDate(date, doseConfig.minDate, doseConfig.maxDate),
    // Return as dayjs objects because MUI DatePicker v6+ expects dayjs when
    // AdapterDayjs is used. Raw Dates would cause adapter mismatch warnings.
    minDate: dayjs(doseConfig.minDate),
    maxDate: dayjs(doseConfig.maxDate)
  };
};

/**
 * Validate a dose date selected by the user against the dose's window.
 *
 * This is called on the onChange of the DatePicker to give immediate feedback
 * if the user somehow bypasses the shouldDisableDate constraint (e.g., by
 * typing directly into the text field).
 *
 * @param {string|null} selectedDate - ISO date string selected by the user
 * @param {Object}      doseConfig   - DoseConfig from getChildPreviousDoseStatus
 * @returns {{ isValid: boolean, reason: string|null }}
 */
export const validateDoseDate = (selectedDate, doseConfig) => {
  if (!selectedDate) {
    return { isValid: false, reason: 'Date is required' };
  }

  const date = dayjs(selectedDate);
  if (!date.isValid()) {
    return { isValid: false, reason: 'Invalid date format' };
  }

  if (!doseConfig.minDate || !doseConfig.maxDate) {
    return { isValid: false, reason: 'Date range not available for this dose' };
  }

  if (date.isBefore(dayjs(doseConfig.minDate), 'day')) {
    return {
      isValid: false,
      reason: `Date is too early. Minimum: ${dayjs(doseConfig.minDate).format('DD/MM/YYYY')}`
    };
  }

  if (date.isAfter(dayjs(doseConfig.maxDate), 'day')) {
    return {
      isValid: false,
      reason: `Date is too late. Maximum: ${dayjs(doseConfig.maxDate).format('DD/MM/YYYY')}`
    };
  }

  return { isValid: true, reason: null };
};
